import { Fragment, useEffect, useState } from "react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { api, errMsg, fmtDate, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ST = { pending: "bg-orange-50 text-orange-700 border-orange-200", approved: "bg-emerald-50 text-emerald-700 border-emerald-200", rejected: "bg-slate-100 text-slate-600 border-slate-300" };

function Requests() {
  const { user, version, bump } = useAuth();
  const [rows, setRows] = useState([]);
  const load = () => api.get("/reversals").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, [version]); // eslint-disable-line react-hooks/exhaustive-deps
  const decide = async (id, action) => {
    try {
      await api.post(`/reversals/${id}/${action}`, { note: "" });
      toast.success(action === "approve" ? "Approved & reversed" : "Rejected");
      bump();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="overflow-auto border border-slate-200 rounded-xl bg-white">
      <table className="dense-table" data-testid="reversal-requests-table">
        <thead><tr><th>Requested</th><th>Txn</th><th>Ledger</th><th>Type</th><th>Txn Date</th><th>Reason</th><th>By</th><th>Status</th><th /></tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={9} className="text-center text-slate-400 py-8">No reversal requests</td></tr>}
          {rows.map((r) => (
            <tr key={r.id} data-testid={`reversal-request-${r.id}`}>
              <td className="num text-xs">{fmtDateTime(r.created_at)}</td><td className="num font-semibold">{r.transaction_id}</td>
              <td className="uppercase text-xs">{r.ledger}</td><td>{r.transaction_type}</td><td className="num">{fmtDate(r.transaction_date)}</td>
              <td className="whitespace-normal min-w-[200px]">{r.reason}</td><td>{r.requested_by_name}</td>
              <td><span className={`text-[11px] px-1.5 py-0.5 rounded border ${ST[r.status]}`}>{r.status}</span></td>
              <td>{user.role === "admin" && r.status === "pending" && (
                <div className="flex gap-1">
                  <Button size="sm" className="h-7 bg-emerald-600 hover:bg-emerald-700" onClick={() => decide(r.id, "approve")} data-testid={`approve-${r.id}`}>Approve</Button>
                  <Button size="sm" variant="outline" className="h-7" onClick={() => decide(r.id, "reject")} data-testid={`reject-${r.id}`}>Reject</Button>
                </div>
              )}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AuditLog() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(null);
  useEffect(() => { api.get("/audit").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  return (
    <div className="overflow-auto max-h-[70vh] border border-slate-200 rounded-xl bg-white">
      <table className="dense-table" data-testid="audit-log-table">
        <thead><tr><th>Time</th><th>User</th><th>Action</th><th>Entity</th><th>ID</th><th>Reason</th><th /></tr></thead>
        <tbody>
          {rows.map((r) => (
            <Fragment key={r.id}>
              <tr>
                <td className="num text-xs">{fmtDateTime(r.timestamp)}</td><td>{r.user_name}</td>
                <td><span className="text-[11px] px-1.5 py-0.5 rounded border bg-slate-50">{r.action}</span></td>
                <td>{r.entity}</td><td className="num text-xs">{r.entity_id}</td><td className="max-w-[220px] truncate">{r.reason}</td>
                <td>{(r.before || r.after) && <button className="text-xs text-sky-700 hover:underline" onClick={() => setOpen(open === r.id ? null : r.id)} data-testid={`audit-toggle-${r.id}`}>{open === r.id ? "Hide" : "Before/After"}</button>}</td>
              </tr>
              {open === r.id && (
                <tr><td colSpan={7} className="bg-slate-50">
                  <div className="grid md:grid-cols-2 gap-3 text-[11px] num whitespace-pre-wrap">
                    <pre className="overflow-auto max-h-64"><b>Before</b>{"\n"}{JSON.stringify(r.before, null, 1)}</pre>
                    <pre className="overflow-auto max-h-64"><b>After</b>{"\n"}{JSON.stringify(r.after, null, 1)}</pre>
                  </div>
                </td></tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Approvals() {
  const { user } = useAuth();
  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">Approvals & Audit</h1>
        <span className="hint-tag mt-2">Entries kabhi delete nahi hoti — reversal/correction entry aur reason maintain hota hai</span>
      </div>
      <Tabs defaultValue="requests">
        <TabsList>
          <TabsTrigger value="requests" data-testid="approvals-tab-requests">Reversal Requests</TabsTrigger>
          {user.role === "admin" && <TabsTrigger value="audit" data-testid="approvals-tab-audit">Audit Log</TabsTrigger>}
        </TabsList>
        <TabsContent value="requests" className="mt-4"><Requests /></TabsContent>
        {user.role === "admin" && <TabsContent value="audit" className="mt-4"><AuditLog /></TabsContent>}
      </Tabs>
    </div>
  );
}
